/**
 * What clicking "Start Whiteboard" should do.
 *
 * There are two buttons that start a board — the Apps menu, and the "Start
 * Whiteboard" button on the approval popup the host's decision produces — and
 * they disagreed. The menu checked the presenter gate and explained itself; the
 * popup called `whiteboard.start()` and threw the answer away.
 *
 * `start()` returns `'not_presenter' | 'already_open' | null`. Ignoring that
 * return is the bug: a host who approves a whiteboard request and then turns
 * the participant's Presenter permission off again leaves them holding a popup
 * whose one button does nothing at all. No toast, no error, no explanation —
 * the click is swallowed by an early return three files away.
 *
 * The rule lives here so both buttons ask the same question and get the same
 * answer, and so the answer can be tested without a meeting.
 *
 * ## Why revocation is not just "ask again"
 *
 * Losing a permission you were granted is not the same as never having had one.
 * Reopening the request dialog is right for an attendee who has never been a
 * presenter; for someone whose grant was just withdrawn it silently fires off a
 * fresh request and tells them nothing about what changed. They are left
 * guessing why a button that worked a moment ago stopped.
 *
 * So the first click after a revocation explains it, and only the next one asks
 * again. That keeps the remedy one click away rather than removing it — being
 * told "your permission was turned off" with no way to ask for it back would
 * trade one dead end for another.
 */

export type WhiteboardClick =
  /** Presenter, and the board is free. Send `whiteboard:start`. */
  | 'start'
  /** Never had presenter, or has been told already. Open the request dialog. */
  | 'request'
  /** Had presenter, lost it, and does not know yet. Say so. */
  | 'explain-revoked'
  /** Presenter, but someone else's board is already on the stage. */
  | 'explain-already-open'

export type WhiteboardGateInput = {
  /** Does this participant hold presenter right now? */
  canPresent: boolean
  /** Has it ever been true during this meeting? */
  everHadPresenter: boolean
  /** Has the revocation already been explained since it happened? */
  explainedRevocation: boolean
  /** Is a board open that somebody else started? */
  openByOther: boolean
}

export function decideWhiteboardClick({
  canPresent,
  everHadPresenter,
  explainedRevocation,
  openByOther,
}: WhiteboardGateInput): WhiteboardClick {
  if (!canPresent) {
    // Checked before `openByOther` for the same reason `start()` refuses
    // `not_presenter` first: not being allowed to open a board outranks the
    // board being taken, and answering with the wrong one sends the
    // participant chasing a problem that is not theirs.
    return everHadPresenter && !explainedRevocation ? 'explain-revoked' : 'request'
  }
  return openByOther ? 'explain-already-open' : 'start'
}

/**
 * Track whether presenter has ever been held, and whether the current loss of
 * it has been explained.
 *
 * A plain latch rather than a diff of the previous value: a role rebroadcast
 * can arrive repeatedly with the same content, and "has this ever been true"
 * does not care how many times it was said. Re-granting clears the explained
 * flag, so a second revocation is explained afresh instead of being treated as
 * old news.
 */
export type PresenterHistory = {
  readonly everHadPresenter: boolean
  readonly explainedRevocation: boolean
}

export const noPresenterHistory = (): PresenterHistory => ({
  everHadPresenter: false,
  explainedRevocation: false,
})

/** Fold the current permission in. Returns the same object when nothing moved. */
export function observePresenter(prev: PresenterHistory, canPresent: boolean): PresenterHistory {
  if (!canPresent) return prev
  return prev.everHadPresenter && !prev.explainedRevocation
    ? prev
    : { everHadPresenter: true, explainedRevocation: false }
}

/** Record that the participant has now been told. */
export function markRevocationExplained(prev: PresenterHistory): PresenterHistory {
  return prev.explainedRevocation ? prev : { ...prev, explainedRevocation: true }
}
